import { TYPE_PERCENTAGE } from './couponAdapter';

/**
 * Derived coupon status, plus the filtering and sorting the list runs on it.
 *
 * The five states are not stored — the API keeps a single `status` flag — so they are computed
 * here. Each one mirrors a condition the backend actually enforces at redemption, so a chip on
 * this page and the answer a family gets at checkout cannot disagree:
 *
 *   Paused         status !== 1                         couponService.validateCoupon
 *   Limit reached  couponUsedByUser >= noOfTimesUsed    couponFacade.couponValidation
 *   Scheduled      startDate > now                      couponService.validateCoupon
 *   Expired        endDate < now                        couponService.validateCoupon
 *
 * Order matters: a paused coupon that is also expired reads as Paused, because that is the state
 * the director changed and the one they can undo.
 *
 * Note the end-date boundary. The server stores end dates through `appUtils.getTimeStampEnd`,
 * which pins them to 23:59:59, so a coupon is live *through* its final day. Comparing against
 * `Date.now()` reproduces that; comparing formatted date strings — as the prototype did — would
 * mark a still-redeemable coupon Expired for its whole last day.
 */

export const STATUS_ACTIVE = 'Active';
export const STATUS_SCHEDULED = 'Scheduled';
export const STATUS_LIMIT_REACHED = 'Limit reached';
export const STATUS_PAUSED = 'Paused';
export const STATUS_EXPIRED = 'Expired';

export const STATUS_ORDER = [
    STATUS_ACTIVE,
    STATUS_SCHEDULED,
    STATUS_LIMIT_REACHED,
    STATUS_PAUSED,
    STATUS_EXPIRED,
];

export const STATUS_STYLES = {
    [STATUS_ACTIVE]: { dot: 'cpn-bg-emerald-500', chip: 'cpn-bg-emerald-50 cpn-text-emerald-700' },
    [STATUS_SCHEDULED]: { dot: 'cpn-bg-sky-500', chip: 'cpn-bg-sky-50 cpn-text-sky-700' },
    [STATUS_LIMIT_REACHED]: { dot: 'cpn-bg-amber-500', chip: 'cpn-bg-amber-50 cpn-text-amber-700' },
    [STATUS_PAUSED]: { dot: 'cpn-bg-neutral-400', chip: 'cpn-bg-neutral-100 cpn-text-neutral-600' },
    [STATUS_EXPIRED]: { dot: 'cpn-bg-neutral-400', chip: 'cpn-bg-neutral-100 cpn-text-neutral-600' },
};

/** How far ahead "Expiring soon" looks. */
export const EXPIRING_WINDOW_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export const getCouponStatus = (coupon, now = Date.now()) => {
    if (!coupon.active) return STATUS_PAUSED;
    if (coupon.possibleUsage !== null && coupon.redeemed >= coupon.possibleUsage) {
        return STATUS_LIMIT_REACHED;
    }
    if (coupon.startDate && coupon.startDate > now) return STATUS_SCHEDULED;
    if (coupon.endDate && coupon.endDate < now) return STATUS_EXPIRED;
    return STATUS_ACTIVE;
};

/**
 * Whole days from `now` until the coupon's end date, or null when it has none.
 *
 * Rounded up so the last partial day counts as one: a coupon ending at 23:59:59 tonight is
 * "Ends today" (0) rather than a fraction that floors to 0 only by luck of the clock.
 */
export const daysUntilExpiry = (coupon, now = Date.now()) => {
    if (!coupon.endDate) return null;
    return Math.ceil((coupon.endDate - now) / DAY_MS) - 1;
};

export const isExpiringSoon = (coupon, now = Date.now()) => {
    if (getCouponStatus(coupon, now) !== STATUS_ACTIVE) return false;
    const days = daysUntilExpiry(coupon, now);
    return days !== null && days >= 0 && days <= EXPIRING_WINDOW_DAYS;
};

/* ---------- formatting ---------- */

export const formatCouponValue = (type, value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return type === TYPE_PERCENTAGE
        ? `${numeric}% off`
        : `$${numeric.toLocaleString()} off`;
};

export const formatCouponAmount = coupon =>
    coupon.type === TYPE_PERCENTAGE ? `${coupon.value}%` : `$${coupon.value.toLocaleString()}`;

/* ---------- list state ---------- */

export const filterCoupons = (items, filters) => {
    const term = filters.query.trim().toLowerCase();
    return items.filter(coupon => {
        const matchesTerm =
            !term ||
            coupon.name.toLowerCase().includes(term) ||
            coupon.code.toLowerCase().includes(term);
        const matchesStatus = filters.status === 'All' || getCouponStatus(coupon) === filters.status;
        const matchesType = filters.type === 'All' || coupon.type === filters.type;
        const matchesExpiring = !filters.expiringOnly || isExpiringSoon(coupon);
        return matchesTerm && matchesStatus && matchesType && matchesExpiring;
    });
};

export const sortCoupons = (items, sort) => {
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
        switch (sort.key) {
            case 'name':
                return a.name.localeCompare(b.name) * factor;
            case 'redeemed':
                return (a.redeemed - b.redeemed) * factor;
            case 'value':
                return (a.value - b.value) * factor;
            case 'status':
                return (
                    (STATUS_ORDER.indexOf(getCouponStatus(a)) -
                        STATUS_ORDER.indexOf(getCouponStatus(b))) *
                    factor
                );
            default:
                // 'created' — the order getCouponsList already returns ($sort created: -1), so
                // ascending here means oldest first.
                return ((a.created || 0) - (b.created || 0)) * factor;
        }
    });
};

/* ---------- code generation and near-duplicate detection ---------- */

export const generateCode = name =>
    name
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '')
        .slice(0, 20);

/**
 * A clone's starting name and code — "Summer 25 (copy)", "SUMMER252" — bumping the suffix until
 * nothing loaded collides.
 *
 * Ported from the prototype's `duplicateName`/`duplicateCode`, with one change: the code
 * comparison is case-insensitive, because `couponModel` indexes `couponCode` without a collation.
 *
 * Both are only suggestions. Several coupons may share a code (checkout applies whichever gives
 * the bigger discount), so the clone form leaves the code unlocked and a director can set it back
 * to the source's code. `existing` is only the coupons this camp has loaded.
 */
export const duplicateName = (name, existing) => {
    const base = name.replace(/\s+\(copy(?:\s\d+)?\)$/i, '');
    // Hoisted out of the loop: closing over a variable the loop reassigns is safe only because
    // `.some` runs synchronously, and writing it that way trips no-loop-func for a reader to
    // re-derive every time.
    const taken = value => existing.some(coupon => coupon.name === value);

    let candidate = `${base} (copy)`;
    let counter = 2;
    while (taken(candidate)) {
        candidate = `${base} (copy ${counter})`;
        counter += 1;
    }
    return candidate;
};

export const duplicateCode = (code, existing) => {
    const taken = value =>
        existing.some(coupon => coupon.code.toLowerCase() === value.toLowerCase());

    let counter = 2;
    let candidate = `${code}${counter}`;
    while (taken(candidate)) {
        counter += 1;
        candidate = `${code}${counter}`;
    }
    return candidate;
};

const normalizeName = name =>
    name
        .toLowerCase()
        .replace(/\(copy(?:\s\d+)?\)/g, '')
        .replace(/[^a-z0-9]/g, '');

/**
 * Finds an existing coupon whose name is the same as, or a prefix of, the one being typed.
 *
 * Only a nudge in the create form — duplicate names and duplicate codes are both legal. Short names
 * are skipped because a 4-character prefix matches almost anything.
 */
export const findSimilarCoupon = (name, existing) => {
    const target = normalizeName(name);
    if (target.length < 5) return null;

    let best = null;
    existing.forEach(coupon => {
        const other = normalizeName(coupon.name);
        if (!other) return;
        if (other === target) {
            if (!best || best.score < 100) best = { coupon, score: 100 };
            return;
        }
        if (other.startsWith(target) || target.startsWith(other)) {
            const score = Math.min(other.length, target.length);
            if (score >= 5 && (!best || score > best.score)) best = { coupon, score };
        }
    });

    return best ? best.coupon : null;
};
